import React, { forwardRef } from 'react'
import { DatePicker as AntDatePicker, DatePickerProps } from 'antd'
import type { RangePickerProps } from 'antd/es/date-picker'
import dayjs, { Dayjs } from 'dayjs'
import { parseDate, formatAsDate } from '../../config/dayjsConfig'

// Custom props that reflect backend string format instead of Dayjs
export interface BackendDatePickerProps extends Omit<DatePickerProps, 'value' | 'onChange'> {
  value?: string | null
  onChange?: (value: string | undefined, dateString: string | string[]) => void
}

export interface BackendRangePickerProps extends Omit<RangePickerProps, 'value' | 'onChange'> {
  value?: [string | null, string | null] | null
  onChange?: (value: [string | undefined, string | undefined] | null, dateStrings: [string, string]) => void
}

const DatePickerComponent = forwardRef<any, BackendDatePickerProps>(({ value, onChange, ...props }, ref) => {
  // Handle value conversion for display - automatically parse string values from backend
  const displayValue = React.useMemo(() => {
    if (typeof value === 'string') {
      // If YYYY-MM-DD format (date only), use dayjs directly without timezone conversions
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return dayjs(value)
      }
      // If ISO timestamp (includes time), use parseDate
      return parseDate(value)
    }
    return value
  }, [value])

  // Handle change event conversion for backend - automatically format for backend
  const handleChange = React.useCallback(
    (date: Dayjs | null, dateString: string | string[]) => {
      // Convert Dayjs to backend format (YYYY-MM-DD string)
      const backendValue = formatAsDate(date)
      onChange?.(backendValue, dateString)
    },
    [onChange]
  )

  return <AntDatePicker ref={ref} format="DD.MM.YYYY" {...props} value={displayValue} onChange={handleChange} />
})

DatePickerComponent.displayName = 'DatePicker'

// Custom RangePicker with timezone support
const RangePickerComponent = forwardRef<any, BackendRangePickerProps>(({ value, onChange, ...props }, ref) => {
  // Handle value conversion for display - automatically parse string values from backend
  const displayValue = React.useMemo(() => {
    if (!Array.isArray(value)) return value

    return value.map(date => {
      if (typeof date === 'string') {
        // If YYYY-MM-DD format (date only), use dayjs directly without timezone conversions
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return dayjs(date)
        }
        // If ISO timestamp (includes time), use parseDate
        return parseDate(date)
      }
      return date
    }) as [Dayjs | null, Dayjs | null]
  }, [value])

  // Handle change event conversion for backend - automatically format for backend
  const handleChange = React.useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => {
      // Convert Dayjs array to backend format (YYYY-MM-DD strings or undefined)
      const backendValue = dates
        ? ([formatAsDate(dates[0]), formatAsDate(dates[1])] as [string | undefined, string | undefined])
        : null
      onChange?.(backendValue, dateStrings)
    },
    [onChange]
  )

  return (
    <AntDatePicker.RangePicker ref={ref} format="DD.MM.YYYY" {...props} value={displayValue} onChange={handleChange} />
  )
})

RangePickerComponent.displayName = 'RangePicker'

// Create a wrapper that includes RangePicker
export const DatePicker = Object.assign(DatePickerComponent, {
  RangePicker: RangePickerComponent
})

export default DatePicker
